<template>
    <UModal
        v-model:open="visible"
        :title="title || $t('general.confirm')"
        :ui="{ content: 'mx-4 w-full md:w-2/4 lg:w-2/5 max-w-none' }"
        @update:open="(open: boolean) => { if (!open) settle(false) }"
    >
        <template #body>
            <p class="text-sm text-default">{{ message }}</p>
        </template>

        <template #footer>
            <div class="flex w-full justify-end gap-2">
                <UButton :label="$t('general.cancel')" color="neutral" variant="subtle" @click="settle(false)" />
                <UButton :label="confirmLabel || $t('general.confirm')" color="error" @click="settle(true)" />
            </div>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
    import { ref } from 'vue'

    /*
     * The house confirmation dialog (vue-spec/components.md): a parent awaits
     * confirm(message) and gets back true or false.
     *
     * The promise is what makes it worth a component. The alternative — a dialog that
     * emits an event and a parent holding on to what it was about to do — spreads one
     * decision over three places; here the destructive call stays in the one function
     * that wanted to make it, which simply stops if the answer is no.
     */
    const visible      = ref(false)
    const title        = ref('')
    const message      = ref('')
    const confirmLabel = ref('')

    /*
     * Resolved by whichever comes first: a button, the escape key, or a click
     * outside. Cleared as it is called, so closing the dialog after it has already
     * answered — which @update:open does on every close, including the one a button
     * just caused — cannot answer a second time.
     */
    const answer = ref<((confirmed: boolean) => void) | null>(null)

    const settle = (confirmed: boolean) => {
        visible.value = false

        const resolve = answer.value
        answer.value  = null
        resolve?.(confirmed)
    }

    const confirm = (text: string, options?: { title?: string, confirmLabel?: string }): Promise<boolean> => {
        // A dialog asked to open while it is still waiting on an answer: the question
        // on screen is being replaced, so the caller behind it is told no.
        settle(false)

        message.value      = text
        title.value        = options?.title ?? ''
        confirmLabel.value = options?.confirmLabel ?? ''
        visible.value      = true

        return new Promise<boolean>((resolve) => { answer.value = resolve })
    }

    defineExpose({ confirm })
</script>
